import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getDocumentLibrary, acknowledgeDocument, GetDocumentLibraryOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { FileText, Search, CheckCircle, AlertTriangle, Clock, ExternalLink } from 'lucide-react';

export default function DocumentLibrary() {
  const { user } = useAuth();
  const [data, setData] = useState<GetDocumentLibraryOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getDocumentLibrary({ search: search || undefined, category: catFilter || undefined }).then(setData).finally(() => setLoading(false)); };
  useEffect(() => { if (user) load(); }, [search, catFilter]);

  const today = new Date().toISOString().split('T')[0];
  const docs = data?.documents || [];
  const policies = data?.policies || [];
  const sigs = data?.signatures || [];

  const hasSigned = (policyName: string) => sigs.some((s: any) => s.policyName === policyName && s.signed);

  const handleAck = async (policy: any) => {
    try {
      await acknowledgeDocument({ policyName: policy.policyName, policyVersion: policy.version || '1.0' });
      toast.success('Policy acknowledged');
      load();
    } catch { toast.error('Failed'); }
  };

  const needsAck = policies.filter(p => p.requiresAcknowledgement && !hasSigned(p.policyName || ''));
  const outdatedDocs = docs.filter(d => d.reviewDate && d.reviewDate < today);

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-primary" /> Document Library</h1>
          <p className="text-sm text-muted-foreground mt-1">Staff documents, policies & acknowledgements</p>
        </div>
      </div>

      {needsAck.length > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">{needsAck.length} {needsAck.length === 1 ? 'policy requires' : 'policies require'} your acknowledgement</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {needsAck.map(p => (
                <Button key={p.id} size="sm" variant="outline" onClick={() => handleAck(p)}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> {p.policyName}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {['Training','Policy','SOP','HR','Health & Safety','General'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="documents">
        <TabsList>
          <TabsTrigger value="documents">Documents ({docs.length})</TabsTrigger>
          <TabsTrigger value="policies">Policies ({policies.length})</TabsTrigger>
          {data?.isManager && <TabsTrigger value="outdated">Outdated ({outdatedDocs.length})</TabsTrigger>}
          {data?.isManager && <TabsTrigger value="ack-status">Acknowledgement Status</TabsTrigger>}
        </TabsList>

        <TabsContent value="documents" className="space-y-3 mt-4">
          {docs.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No documents</p> : docs.map((d: any) => (
            <Card key={d.id}>
              <CardContent className="py-4 flex flex-col sm:flex-row justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{d.title}</h3>
                    {d.documentCategory && <Badge variant="outline" className="text-xs">{d.documentCategory}</Badge>}
                    {d.documentType && <Badge variant="outline" className="text-xs">{d.documentType}</Badge>}
                    {d.version && <Badge variant="outline" className="text-xs">v{d.version}</Badge>}
                    {d.requiresAcknowledgement && <Badge className="bg-primary/10 text-primary text-xs">Needs Ack</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {d.uploadDate && <span>Uploaded: {new Date(d.uploadDate).toLocaleDateString()}</span>}
                    {d.expiryDate && <span className={d.expiryDate < today ? 'text-red-600' : ''}>Expires: {new Date(d.expiryDate).toLocaleDateString()}</span>}
                    {d.reviewDate && <span className={d.reviewDate < today ? 'text-orange-600' : ''}>Review: {new Date(d.reviewDate).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {d.file?.[0]?.url && <Button size="sm" variant="outline" onClick={() => window.open(d.file[0].url, '_blank')}><ExternalLink className="w-3.5 h-3.5 mr-1" /> View</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="policies" className="space-y-3 mt-4">
          {policies.map((p: any) => {
            const signed = hasSigned(p.policyName || '');
            return (
              <Card key={p.id}>
                <CardContent className="py-4 flex justify-between items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{p.policyName}</h3>
                      {p.version && <Badge variant="outline" className="text-xs">v{p.version}</Badge>}
                      {p.requiresAcknowledgement && (signed ? <Badge className="bg-green-500/10 text-green-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Acknowledged</Badge> : <Badge className="bg-orange-500/10 text-orange-700 text-xs">Needs Acknowledgement</Badge>)}
                    </div>
                    {p.reviewDate && <p className="text-xs text-muted-foreground mt-1">Review: {new Date(p.reviewDate).toLocaleDateString()}</p>}
                  </div>
                  <div className="flex gap-2">
                    {p.attachment?.[0]?.url && <Button size="sm" variant="outline" onClick={() => window.open(p.attachment[0].url, '_blank')}><ExternalLink className="w-3.5 h-3.5" /></Button>}
                    {p.requiresAcknowledgement && !signed && <Button size="sm" onClick={() => handleAck(p)}>Acknowledge</Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {data?.isManager && (
          <TabsContent value="outdated" className="space-y-3 mt-4">
            {outdatedDocs.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No outdated documents</p> : outdatedDocs.map((d: any) => (
              <Card key={d.id} className="border-orange-300/30">
                <CardContent className="py-4 flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{d.title}</h3>
                    <p className="text-xs text-orange-600"><Clock className="w-3 h-3 inline mr-1" />Review overdue: {new Date(d.reviewDate).toLocaleDateString()}</p>
                  </div>
                  <Badge variant="destructive" className="text-xs">Overdue</Badge>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        )}

        {data?.isManager && (
          <TabsContent value="ack-status" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Policy Acknowledgement Summary</CardTitle></CardHeader>
              <CardContent>
                {policies.filter(p => p.requiresAcknowledgement).map((p: any) => (
                  <div key={p.id} className="py-2 border-b border-border last:border-0 flex justify-between items-center">
                    <span className="text-sm font-medium">{p.policyName}</span>
                    <Badge variant="outline" className="text-xs">v{p.version || '1.0'}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
